import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { fmtPrice } from "../lib/i18n";
import { toast } from "sonner";

export default function AdminDashboard() {
    const { lang, t } = useApp();
    const navigate = useNavigate();
    const [tab, setTab] = useState("orders");
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem("sraa_admin_token");
        if (!token) { navigate("/admin/login"); return; }
        api.get("/admin/me").catch(() => navigate("/admin/login"));
        loadAll();
    }, [navigate]);

    const loadAll = async () => {
        try {
            const [o, p, s] = await Promise.all([
                api.get("/admin/orders"),
                api.get("/products"),
                api.get("/admin/settings"),
            ]);
            setOrders(o.data);
            setProducts(p.data);
            setSettings(s.data);
        } catch {
            toast.error("Error loading data");
        }
    };

    const logout = () => {
        localStorage.removeItem("sraa_admin_token");
        navigate("/admin/login");
    };

    const saveSettings = async () => {
        try {
            await api.put("/admin/settings", settings);
            toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
        } catch { toast.error("Error"); }
    };

    const updateProduct = async (id, updates) => {
        try {
            const r = await api.put(`/admin/products/${id}`, updates);
            setProducts((ps) => ps.map((p) => p.id === id ? r.data : p));
            toast.success(lang === "ar" ? "تم تحديث المنتج" : "Product updated");
        } catch { toast.error("Error"); }
    };

    return (
        <div data-testid="admin-dashboard-page" className="pt-32 pb-24 max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
                <div>
                    <div className="overline mb-2">{lang === "ar" ? "المسؤول" : "Admin"}</div>
                    <h1 className="section-heading text-4xl md:text-5xl text-white">{t.admin.dashboard}</h1>
                </div>
                <button onClick={logout} data-testid="admin-logout-btn" className="btn-gold">{t.admin.logout}</button>
            </div>

            <div className="flex gap-2 border-b border-[#D4AF37]/20 mb-8 flex-wrap">
                {[
                    { k: "orders", l: t.admin.orders },
                    { k: "products", l: t.admin.products },
                    { k: "settings", l: t.admin.settings },
                ].map((tb) => (
                    <button
                        key={tb.k}
                        onClick={() => setTab(tb.k)}
                        data-testid={`admin-tab-${tb.k}`}
                        className={`px-5 py-3 text-sm tracking-widest border-b-2 -mb-[1px] ${
                            tab === tb.k ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-white/60 hover:text-white"
                        }`}
                    >
                        {tb.l}
                    </button>
                ))}
            </div>

            {tab === "orders" && (
                <div data-testid="admin-orders-list" className="space-y-4">
                    {orders.length === 0 && <p className="text-white/50">{lang === "ar" ? "لا توجد طلبات" : "No orders"}</p>}
                    {orders.map((o) => (
                        <div key={o.id} className="luxury-card p-6">
                            <div className="flex justify-between items-start gap-4 flex-wrap">
                                <div>
                                    <p className="text-white font-medium">{o.customer_name} · <span className="text-[#D4AF37]">{o.customer_phone}</span></p>
                                    <p className="text-white/50 text-xs mt-1">{o.id}</p>
                                    <p className="text-white/60 text-sm mt-2">{o.customer_address}, {o.customer_city}</p>
                                </div>
                                <div className="text-end">
                                    <p className="price-display text-2xl text-[#D4AF37]">{fmtPrice(o.total, lang)}</p>
                                    <div className="flex gap-2 mt-2 justify-end flex-wrap">
                                        <Badge label={o.payment_method} />
                                        <Badge label={o.payment_status} status />
                                        <Badge label={o.status} />
                                    </div>
                                </div>
                            </div>
                            <div className="hairline my-4" />
                            <ul className="space-y-1 text-sm text-white/80">
                                {o.items.map((it, idx) => (
                                    <li key={idx} className="flex justify-between">
                                        <span>{it.product_name} {it.variant_label && `(${it.variant_label})`} × {it.quantity}</span>
                                        <span className="price-display text-white">{fmtPrice(it.price * it.quantity, lang)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}

            {tab === "products" && (
                <div data-testid="admin-products-list" className="space-y-4">
                    {products.map((p) => (
                        <ProductRow key={p.id} product={p} onSave={updateProduct} lang={lang} />
                    ))}
                </div>
            )}

            {tab === "settings" && settings && (
                <div data-testid="admin-settings-form" className="luxury-card p-8 space-y-4 max-w-2xl">
                    <h2 className="font-serif text-xl text-white mb-4">{lang === "ar" ? "بيانات التحويل البنكي والتواصل" : "Bank & Contact Info"}</h2>
                    {[
                        ["bank_name", lang === "ar" ? "اسم البنك" : "Bank Name"],
                        ["bank_account_name", lang === "ar" ? "اسم الحساب" : "Account Name"],
                        ["bank_iban", "IBAN"],
                        ["bank_swift", "SWIFT"],
                        ["whatsapp", "WhatsApp"],
                        ["instagram", "Instagram"],
                        ["email", "Email"],
                        ["location", lang === "ar" ? "الموقع" : "Location"],
                    ].map(([k, l]) => (
                        <div key={k}>
                            <label className="overline block mb-1">{l}</label>
                            <input
                                className="lux-input"
                                data-testid={`settings-${k}`}
                                value={settings[k] || ""}
                                onChange={(e) => setSettings((s) => ({ ...s, [k]: e.target.value }))}
                            />
                        </div>
                    ))}
                    <button onClick={saveSettings} data-testid="settings-save-btn" className="btn-gold btn-gold-filled mt-4">
                        {lang === "ar" ? "حفظ" : "Save"}
                    </button>
                </div>
            )}
        </div>
    );
}

const Badge = ({ label, status }) => (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${
        status && (label === "paid" ? "border-green-500/40 text-green-400" : "border-[#D4AF37]/40 text-[#D4AF37]")
        || "border-white/20 text-white/70"
    }`}>{label}</span>
);

const ProductRow = ({ product, onSave, lang }) => {
    const [edit, setEdit] = useState(false);
    const [draft, setDraft] = useState(product);
    return (
        <div className="luxury-card p-6" data-testid={`admin-product-${product.slug}`}>
            <div className="flex items-start gap-4 flex-wrap">
                <img src={product.image} alt={product.name_en} className="w-20 h-24 object-cover border border-[#D4AF37]/20" />
                <div className="flex-1 min-w-[200px]">
                    <p className="text-white font-medium">{lang === "ar" ? product.name_ar : product.name_en}</p>
                    <p className="text-white/50 text-xs">{product.slug}</p>
                    {product.variants.length > 0 ? (
                        <div className="mt-2 flex gap-2 flex-wrap">
                            {product.variants.map((v, i) => (
                                <span key={i} className="text-xs text-[#D4AF37] border border-[#D4AF37]/30 px-2 py-0.5">
                                    {v.label} · {v.price} AED
                                </span>
                            ))}
                        </div>
                    ) : (
                        <p className="text-[#D4AF37] mt-2 text-sm">{product.base_price} AED</p>
                    )}
                </div>
                <button onClick={() => setEdit((e) => !e)} className="btn-gold text-xs !py-2 !px-4">
                    {edit ? (lang === "ar" ? "إلغاء" : "Cancel") : (lang === "ar" ? "تعديل" : "Edit")}
                </button>
            </div>
            {edit && (
                <div className="mt-4 space-y-3">
                    <input className="lux-input" value={draft.name_ar} onChange={(e) => setDraft({ ...draft, name_ar: e.target.value })} placeholder="Name (AR)" />
                    <input className="lux-input" value={draft.name_en} onChange={(e) => setDraft({ ...draft, name_en: e.target.value })} placeholder="Name (EN)" />
                    <input className="lux-input" value={draft.image} onChange={(e) => setDraft({ ...draft, image: e.target.value })} placeholder="Image URL" />
                    {draft.variants.length === 0 ? (
                        <input className="lux-input" type="number" value={draft.base_price || 0} onChange={(e) => setDraft({ ...draft, base_price: parseFloat(e.target.value) })} placeholder="Base Price" />
                    ) : (
                        draft.variants.map((v, i) => (
                            <div key={i} className="flex gap-2">
                                <input className="lux-input" value={v.label} onChange={(e) => {
                                    const nv = [...draft.variants]; nv[i] = { ...nv[i], label: e.target.value };
                                    setDraft({ ...draft, variants: nv });
                                }} />
                                <input className="lux-input" type="number" value={v.price} onChange={(e) => {
                                    const nv = [...draft.variants]; nv[i] = { ...nv[i], price: parseFloat(e.target.value) };
                                    setDraft({ ...draft, variants: nv });
                                }} />
                            </div>
                        ))
                    )}
                    <button
                        onClick={() => { onSave(product.id, {
                            name_ar: draft.name_ar, name_en: draft.name_en, image: draft.image,
                            base_price: draft.base_price, variants: draft.variants,
                        }); setEdit(false); }}
                        className="btn-gold btn-gold-filled"
                    >
                        {lang === "ar" ? "حفظ" : "Save"}
                    </button>
                </div>
            )}
        </div>
    );
};
